import { useState, useRef, useCallback } from 'react'

interface VoiceConfig {
    provider: string
    voice: string
    speed: number
    language: string
}

interface UseVoiceProps {
    apiKey?: string
    baseUrl?: string
    voiceConfig?: VoiceConfig
    onTranscript?: (text: string) => void
    enabled?: boolean
}

export function useVoice({ apiKey, baseUrl, voiceConfig, onTranscript, enabled = false }: UseVoiceProps) {
    const [isRecording, setIsRecording] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioChunksRef = useRef<Blob[]>([])
    const currentAudioRef = useRef<HTMLAudioElement | null>(null)
    const recognitionRef = useRef<any>(null)

    const fallbackToWebSpeech = useCallback(() => {
        console.log('Using Web Speech API fallback...')
        
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
            recognitionRef.current = new SpeechRecognition()
            
            recognitionRef.current.continuous = false
            recognitionRef.current.interimResults = false
            recognitionRef.current.lang = voiceConfig?.language === 'en' ? 'en-US' : voiceConfig?.language || 'en-US'
            
            recognitionRef.current.onresult = (event: any) => {
                const transcript = event.results[0][0].transcript
                console.log('Web Speech API result:', transcript)
                onTranscript?.(transcript)
            }
            
            recognitionRef.current.onerror = (event: any) => {
                console.error('Web Speech API error:', event.error)
            }
            
            recognitionRef.current.onend = () => {
                console.log('Web Speech API ended')
            }
            
            recognitionRef.current.start()
            setIsRecording(true)
            
            // Auto-stop Web Speech API after 10 seconds
            setTimeout(() => {
                if (recognitionRef.current) {
                    console.log('Auto-stopping Web Speech API after 10 seconds')
                    recognitionRef.current.stop()
                    setIsRecording(false)
                }
            }, 10000)
        } else {
            console.error('Web Speech API not supported')
        }
    }, [voiceConfig, onTranscript])

    const startRecording = useCallback(async () => {
        console.log('Starting recording...', { enabled, isRecording, apiKey: !!apiKey, baseUrl })
        
        if (!enabled || isRecording) {
            console.log('Recording blocked:', { enabled, isRecording })
            return
        }
        
        // If no API key or baseUrl, use Web Speech API directly
        if (!apiKey || !baseUrl) {
            console.log('No API key or baseUrl, using Web Speech API directly')
            fallbackToWebSpeech()
            return
        }

        try {
            console.log('Requesting microphone access...')
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true
                } 
            })
            
            console.log('Microphone access granted, setting up recorder...')
            audioChunksRef.current = []
            
            // Check supported MIME types
            let mimeType = 'audio/webm;codecs=opus'
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = 'audio/webm'
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    mimeType = 'audio/mp4'
                    if (!MediaRecorder.isTypeSupported(mimeType)) {
                        mimeType = ''
                    }
                }
            }
            
            console.log('Using MIME type:', mimeType)
            mediaRecorderRef.current = new MediaRecorder(stream, mimeType ? { mimeType } : {})
            
            mediaRecorderRef.current.ondataavailable = (event) => {
                console.log('Audio data available:', event.data.size, 'bytes')
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data)
                }
            }
            
            mediaRecorderRef.current.onstop = async () => {
                console.log('Recording stopped, processing audio...')
                setIsProcessing(true)
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' })
                console.log('Audio blob created:', audioBlob.size, 'bytes')
                await processAudio(audioBlob)
                setIsProcessing(false)
                
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop())
            }
            
            mediaRecorderRef.current.onerror = (event) => {
                console.error('MediaRecorder error:', event)
            }
            
            mediaRecorderRef.current.start(1000) // Record in 1-second chunks
            setIsRecording(true)
            console.log('Recording started successfully')
            
            // Auto-stop recording after 10 seconds
            setTimeout(() => {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    console.log('Auto-stopping recording after 10 seconds')
                    mediaRecorderRef.current.stop()
                }
            }, 10000)
        } catch (error) {
            console.error('Failed to start recording:', error)
            setIsRecording(false)
        }
    }, [enabled, isRecording, apiKey, baseUrl])

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop()
            setIsRecording(false)
        }
    }, [isRecording])

    const processAudio = async (audioBlob: Blob) => {
        try {
            console.log('Processing audio blob:', audioBlob.size, 'bytes')
            
            const formData = new FormData()
            formData.append('audio', audioBlob, 'recording.webm')
            formData.append('model', 'whisper-1')
            formData.append('language', voiceConfig?.language || 'en')
            
            console.log('Sending transcription request to:', `${baseUrl}/api/voice/transcribe?apiKey=${apiKey}`)
            
            const response = await fetch(`${baseUrl}/api/voice/transcribe?apiKey=${apiKey}`, {
                method: 'POST',
                body: formData
            })
            
            console.log('Transcription response status:', response.status)
            
            if (response.ok) {
                const data = await response.json()
                console.log('Transcription result:', data)
                if (data.text && data.text.trim()) {
                    console.log('Calling onTranscript with:', data.text.trim())
                    onTranscript?.(data.text.trim())
                } else {
                    console.log('No text in transcription result')
                }
            } else {
                const errorText = await response.text()
                console.error('Transcription failed:', response.status, response.statusText, errorText)
                
                // Fallback to Web Speech API
                console.log('Falling back to Web Speech API...')
                fallbackToWebSpeech()
            }
        } catch (error) {
            console.error('Error processing audio:', error)
        }
    }

    const speak = useCallback(async (text: string) => {
        if (!enabled || !text) return
        
        console.log('Speaking text:', text)
        
        // Stop any currently playing audio
        if (currentAudioRef.current) {
            currentAudioRef.current.pause()
            currentAudioRef.current = null
        }
        
        // If no API key or baseUrl, use Web Speech Synthesis API
        if (!apiKey || !baseUrl) {
            console.log('Using Web Speech Synthesis API')
            const utterance = new SpeechSynthesisUtterance(text)
            utterance.rate = voiceConfig?.speed || 1.0
            utterance.pitch = 1.0
            speechSynthesis.speak(utterance)
            return
        }
        
        try {
            console.log('Using server-side TTS')
            const response = await fetch(`${baseUrl}/api/voice/synthesize?apiKey=${apiKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: text,
                    voice: voiceConfig?.voice || 'alloy',
                    model: voiceConfig?.model || 'tts-1-hd',
                    speed: voiceConfig?.speed || 1.0
                })
            })
            
            if (response.ok) {
                const audioBlob = await response.blob()
                const audioUrl = URL.createObjectURL(audioBlob)
                const audio = new Audio(audioUrl)
                
                currentAudioRef.current = audio
                
                audio.onended = () => {
                    URL.revokeObjectURL(audioUrl)
                    currentAudioRef.current = null
                }
                
                await audio.play()
            } else {
                console.error('Speech synthesis failed, falling back to Web Speech API')
                const utterance = new SpeechSynthesisUtterance(text)
                utterance.rate = voiceConfig?.speed || 1.0
                utterance.pitch = 1.0
                speechSynthesis.speak(utterance)
            }
        } catch (error) {
            console.error('Error synthesizing speech, falling back to Web Speech API:', error)
            const utterance = new SpeechSynthesisUtterance(text)
            utterance.rate = voiceConfig?.speed || 1.0
            utterance.pitch = 1.0
            speechSynthesis.speak(utterance)
        }
    }, [enabled, apiKey, baseUrl, voiceConfig])

    return {
        isRecording,
        isProcessing,
        startRecording,
        stopRecording,
        speak
    }
}